import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  MapPin, CheckSquare, GraduationCap, Users, Calendar, Home as HomeIcon,
  Tag, Activity, Handshake, Globe, Star, ArrowRight, Sparkles, Check,
  FileText, Compass, BadgeCheck, X, ChevronDown, Volume2, VolumeX, Play,
} from 'lucide-react';
import { TIERS } from '../constants/tiers';
import {
  formatSavingsBadge,
  formatPerCardSavings,
} from '../constants/annualSavings';
import { PRICING_CONTENT } from '../constants/pricingContent';
import Header from '../components/Header';
import ReviewsSection from '../components/ReviewsSection';
import { trackViewPricing, trackToolChipClicked } from '../lib/analytics';

// iter 145 (Section 4) - Hero video source (self-hosted Cloudinary MP4
// of Francisco & Jenna, replacing the static Tulum beach house).
// iter 146 - Compressed via Cloudinary `q_auto:low,f_auto` for faster
// first paint + a precise Cloudinary thumbnail (so_2 = frame at 2s)
// as the poster so the right frame shows instantly while it buffers.
const HERO_IMG = 'https://images.unsplash.com/photo-1683914791865-289df4eeb4f8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHx0dWx1bSUyMGJlYWNoJTIwaG91c2UlMjBhcmNoaXRlY3R1cmV8ZW58MHx8fHwxNzc4NTM2NDc2fDA&ixlib=rb-4.1.0&q=85';
const HERO_VIDEO = 'https://res.cloudinary.com/des9fk8et/video/upload/q_auto:low,f_auto/copy_882705EA-DCE1-49F7-964B-E627EA55D68D_ew1ysn.mp4';
const HERO_POSTER = 'https://res.cloudinary.com/des9fk8et/video/upload/so_2/copy_882705EA-DCE1-49F7-964B-E627EA55D68D_ew1ysn.jpg';
const AVATARS = [
  'https://images.unsplash.com/photo-1547121591-ebfd615332af?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwxfHxzbWlsaW5nJTIwZGl2ZXJzZSUyMHBlb3BsZSUyMHBvcnRyYWl0JTIwYXZhdGFyfGVufDB8fHx8MTc3ODUzNjQ2NXww&ixlib=rb-4.1.0&q=85',
  'https://images.unsplash.com/photo-1758613171176-ea64579c2dcf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwzfHxzbWlsaW5nJTIwZGl2ZXJzZSUyMHBlb3BsZSUyMHBvcnRyYWl0JTIwYXZhdGFyfGVufDB8fHx8MTc3ODUzNjQ2NXww&ixlib=rb-4.1.0&q=85',
  'https://images.unsplash.com/photo-1758600435880-d7c0546a69d0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHw0fHxzbWlsaW5nJTIwZGl2ZXJzZSUyMHBlb3BsZSUyMHBvcnRyYWl0JTIwYXZhdGFyfGVufDB8fHx8MTc3ODUzNjQ2NXww&ixlib=rb-4.1.0&q=85',
  'https://images.unsplash.com/photo-1758600436089-0660fce3e7e5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwyfHxzbWlsaW5nJTIwZGl2ZXJzZSUyMHBlb3BsZSUyMHBvcnRyYWl0JTIwYXZhdGFyfGVufDB8fHx8MTc3ODUzNjQ2NXww&ixlib=rb-4.1.0&q=85',
];

/* ============== COST CALC ENTRY (iter 145 Section 4) ==============
   Replaces the prior 3-tool ToolStrip (Move Score / Cost Calc / Visa
   Finder) with a single Cost-of-Living quiz entry card. Same chip
   pattern + cities, but one card centered with the renamed headline. */
function CostCalcEntry() {
  const navigate = useNavigate();
  const [tappedKey, setTappedKey] = useState(null);
  const tap = (key, href, { value }) => {
    trackToolChipClicked({ tool: 'cost_calculator', value });
    setTappedKey(key);
    setTimeout(() => navigate(href), 200);
  };
  const costChips = [
    { id: 'mexico_city',     label: 'Mexico City' },
    { id: 'merida',          label: 'Mérida' },
    { id: 'puerto_vallarta', label: 'Puerto Vallarta' },
    { id: 'oaxaca',          label: 'Oaxaca' },
    { id: 'san_miguel',      label: 'San Miguel' },
    { id: null,              label: 'More cities →' },
  ];
  return (
    <section
      data-testid="home-cost-calc-entry"
      className="bg-[#F9F6EF] py-14 md:py-20 px-6 md:px-10"
    >
      <div className="max-w-3xl mx-auto bg-[#F9F6EF] border border-[#0A1F2E] rounded-2xl p-8 md:p-10">
        <p className="font-mono uppercase tracking-[0.2em] text-[10px] text-[#0A1F2E] mb-3">
          Free Calculator · 3 minutes
        </p>
        <h2
          className="font-display italic text-3xl md:text-5xl text-[#0A1F2E] leading-[1.05] mb-5"
          data-testid="home-cost-calc-headline"
        >
          Find Out What Your Life in Mexico Actually Costs.
        </h2>
        <p className="text-sm md:text-base text-[#0A1F2E]/70 mb-6">
          Pick a city to start - we&rsquo;ll map your visa, your budget, and the neighborhoods that match.
        </p>
        <div className="flex flex-wrap gap-2 mb-6">
          {costChips.map((c) => {
            const key = `cost-${c.id || 'more'}`;
            const tapped = tappedKey === key;
            const href = c.id
              ? `/mexico-cost-of-living-calculator?city=${c.id}`
              : '/mexico-cost-of-living-calculator';
            return (
              <button
                key={key}
                type="button"
                data-testid={`home-cost-chip-${c.id || 'more'}`}
                onClick={() => tap(key, href, { value: c.label })}
                className={`text-sm px-4 py-2 rounded-xl border-2 transition ${
                  tapped
                    ? 'bg-[#E8A830] border-[#E8A830] text-[#0A1F2E]'
                    : 'bg-transparent border-[#0A1F2E] text-[#0A1F2E] hover:bg-[#0A1F2E]/5'
                }`}
              >
                {c.label}
              </button>
            );
          })}
        </div>
        <p className="italic text-[#0A1F2E]/65 text-sm">Calculate your full budget →</p>
      </div>
    </section>
  );
}


/* ============== HERO ============== */
function Hero() {
  // iter 144m - anchor scroll to homepage pricing section.
  const scrollToPricing = (e) => {
    e.preventDefault();
    const el = document.getElementById('pricing');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  // iter 146 - Mute/unmute toggle. Starts muted (browsers require muted
  // for autoplay), tapping the bottom-right speaker enables audio.
  const videoRef = React.useRef(null);
  const [muted, setMuted] = React.useState(true);
  // iter 147 - Play-overlay state. Shows a translucent ▶ overlay only
  // when the video is paused OR autoplay was blocked. Drives off the
  // <video> element's native play/pause events so it stays accurate.
  const [showPlayOverlay, setShowPlayOverlay] = React.useState(false);
  React.useEffect(() => {
    const v = videoRef.current;
    if (!v) return undefined;
    const onPlay  = () => setShowPlayOverlay(false);
    const onPause = () => setShowPlayOverlay(true);
    v.addEventListener('play', onPlay);
    v.addEventListener('pause', onPause);
    // Autoplay-block detector: try to play; if rejected, surface the
    // overlay so the user can tap to start playback.
    const tryPlay = () => {
      const p = v.play();
      if (p && typeof p.then === 'function') {
        p.catch(() => setShowPlayOverlay(true));
      }
    };
    tryPlay();
    return () => {
      v.removeEventListener('play', onPlay);
      v.removeEventListener('pause', onPause);
    };
  }, []);
  const handlePlayOverlayClick = () => {
    const v = videoRef.current;
    if (!v) return;
    try { v.play().catch(() => {}); } catch { /* noop */ }
  };
  const toggleMuted = () => {
    const v = videoRef.current;
    if (!v) return;
    const next = !muted;
    v.muted = next;
    if (!next) {
      try { v.play().catch(() => {}); } catch { /* noop */ }
    }
    setMuted(next);
  };
  return (
    <header
      data-testid="hero-section"
      className="relative w-full min-h-[88vh] md:min-h-[100vh] flex items-end overflow-hidden -mt-16 md:-mt-20"
      style={{ backgroundColor: '#1a2b2a' }}
      aria-label="Mundalo - Move Abroad. Live Freely."
    >
      {/* iter 145 - Hero video. Self-hosted MP4 from Cloudinary, autoplay
          muted loop with playsinline. iter 146 - Compressed q_auto:low
          variant + Cloudinary thumbnail poster (so_2 = frame at 2s) so
          the right frame paints instantly while the video buffers.
          Static HERO_IMG sits below as a last-line fallback if the video
          URL itself 404s. Container bg #1a2b2a guarantees no white flash. */}
      <img
        src={HERO_IMG}
        alt=""
        aria-hidden="true"
        className="absolute inset-0 w-full h-full object-cover"
        loading="eager"
        fetchPriority="high"
      />
      <video
        ref={videoRef}
        src={HERO_VIDEO}
        poster={HERO_POSTER}
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        aria-hidden="true"
        className="absolute inset-0 w-full h-full object-cover pointer-events-none"
        onError={(e) => { e.currentTarget.style.display = 'none'; }}
      />
      {/* Dark gradient overlay - guarantees text is readable regardless of background photo */}
      <div
        className="absolute inset-0"
        aria-hidden="true"
        style={{
          background:
            'linear-gradient(to bottom, rgba(0,0,0,0.15) 0%, rgba(10,31,46,0.65) 60%, rgba(10,31,46,0.85) 100%)',
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto w-full px-6 md:px-10 pb-16 md:pb-24 pt-32 md:pt-40">
        <p
          data-testid="hero-eyebrow"
          className="font-mono uppercase tracking-[0.18em] text-[10px] md:text-xs text-white mb-5 md:mb-7"
        >
          The Relocation Platform · Mexico
        </p>

        <h1
          data-testid="hero-heading"
          className="font-display italic text-white leading-[0.95] tracking-[-0.015em] text-[64px] sm:text-[80px] md:text-[96px] lg:text-[112px] max-w-5xl"
          style={{ textShadow: '0 2px 24px rgba(0,0,0,0.35)' }}
        >
          Moving to Mexico?<br />Here's your plan.
        </h1>

        <p
          data-testid="hero-subhead"
          className="font-body font-light text-lg md:text-2xl text-white mt-6 md:mt-8 max-w-2xl leading-relaxed"
          style={{ textShadow: '0 1px 12px rgba(0,0,0,0.35)' }}
        >
          A personalized Blueprint for your visa, your city, your budget, and your family. Built for Americans making the move for real.
        </p>

        {/* iter 144e - hero CTAs:
            • Primary (Get Your Blueprint): amber filled, semibold for full
              visual weight, w-full on mobile.
            • Secondary (Find Your Readiness Score): ghost outline with
              light font-weight + softened border so the hierarchy gap
              against the primary is unmistakable.
            • Mobile: stacked column, exact 12px (gap-3) between buttons,
              each full-width.
            • Desktop: row with a generous 24px (gap-6) gap. */}
        <div className="mt-8 md:mt-10 flex flex-col sm:flex-row gap-3 sm:gap-6">
          <Link
            to="/relocation/blueprint"
            data-testid="hero-cta-primary"
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#E8A830] hover:brightness-95 text-[#0A1F2E] font-semibold px-7 py-4 rounded-full transition-all hover:-translate-y-0.5 shadow-sm"
          >
            Get Your Blueprint <ArrowRight className="w-4 h-4" />
          </Link>
          {/* iter 145 - Move Score secondary CTA removed per Section 4
              (Move Score tool stripped from homepage). */}
        </div>

        {/* iter 144m - lightweight pricing anchor link, amber italic. */}
        <a
          href="#pricing"
          onClick={scrollToPricing}
          data-testid="hero-pricing-anchor"
          className="mt-5 inline-flex items-center gap-1.5 font-display italic text-[#E8A830] hover:text-[#f5be4d] text-sm md:text-base transition-colors"
        >
          or see pricing
          <ChevronDown className="w-3.5 h-3.5" aria-hidden="true" />
        </a>
      </div>

      {/* iter 147 - Play overlay. Translucent triangle that fades in
          only when the video is paused or autoplay was blocked. Sits
          above the gradient (z-15) so it stays tappable. Pointer-events
          flip to none when hidden so it never blocks the headline CTAs. */}
      <button
        type="button"
        onClick={handlePlayOverlayClick}
        aria-label="Play hero video"
        data-testid="hero-play-overlay"
        className={`absolute inset-0 z-[15] flex items-center justify-center transition-opacity duration-500 ease-out ${
          showPlayOverlay ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      >
        <span
          aria-hidden="true"
          className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-white/15 hover:bg-white/22 backdrop-blur-sm border border-white/35 flex items-center justify-center transition-colors"
        >
          {/* Slight right-shift so the play triangle visually centers */}
          <Play className="w-9 h-9 md:w-11 md:h-11 text-white fill-white translate-x-0.5" />
        </span>
      </button>

      {/* iter 146 - Subtle mute/unmute toggle, bottom-right of hero.
          Sits above the gradient overlay (z-20) so it stays clickable
          but doesn't compete with the CTAs. */}
      <button
        type="button"
        onClick={toggleMuted}
        aria-label={muted ? 'Unmute hero video' : 'Mute hero video'}
        data-testid="hero-mute-toggle"
        className="absolute bottom-5 right-5 md:bottom-7 md:right-7 z-20 w-10 h-10 md:w-11 md:h-11 rounded-full flex items-center justify-center bg-black/35 hover:bg-black/55 backdrop-blur-sm border border-white/25 text-white transition-colors"
      >
        {muted ? <VolumeX className="w-4 h-4 md:w-5 md:h-5" /> : <Volume2 className="w-4 h-4 md:w-5 md:h-5" />}
      </button>
    </header>
  );
}

/* ============== ANNOUNCEMENT BANNER ==============
   iter 174 - Venezuela earthquake relief banner. Commitment is
   ACTIVE and being fulfilled (50% of net proceeds). iter 187.i -
   Repositioned from above the top nav to a dedicated section above
   the footer so it reads as its own visually distinct block rather
   than a persistent header strip. Full treatment retained (copy +
   "Donate Now" button); nothing shrunk to a text link. Remove from
   code only when the commitment is closed. */
function PromoBanner() {
  return (
    <section
      data-testid="promo-banner"
      role="region"
      aria-label="Venezuela earthquake relief"
      className="w-full bg-[#2CA8A0] text-white py-8 md:py-10"
    >
      <div className="max-w-4xl mx-auto px-6 md:px-10 flex flex-col md:flex-row items-center justify-between gap-5 md:gap-8 text-center md:text-left">
        <div className="flex-1">
          <p
            data-testid="promo-banner-eyebrow"
            className="font-mono uppercase tracking-[0.18em] text-[11px] text-white/80 mb-2"
          >
            Our giving commitment
          </p>
          <p
            data-testid="promo-banner-text"
            className="font-display text-lg md:text-xl leading-snug"
          >
            Mundalo is donating 50% of net proceeds to Venezuela earthquake relief - you can also donate directly. 🇻🇪
          </p>
        </div>
        <a
          href="https://globalempowermentmission.org/mission/venezuela-earthquake/"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="promo-banner-cta"
          className="flex-shrink-0 inline-flex items-center px-6 py-3 bg-white text-[#2CA8A0] text-sm font-semibold rounded-full hover:bg-[#F9F6EF] transition"
        >
          Donate Now
        </a>
      </div>
    </section>
  );
}

/* ============== SOCIAL PROOF BAR ============== */
function SocialProof() {
  return (
    <section
      data-testid="social-proof"
      className="bg-[#0A1F2E] py-12 md:py-16 relative overflow-hidden"
      aria-label="Brand statement"
    >
      {/* subtle cream-tinted noise wash so the deep ocean reads as a finished surface, not a flat block */}
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-[0.06] pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(circle at 20% 30%, #F4ECD8 0%, transparent 55%), radial-gradient(circle at 80% 70%, #2CA8A0 0%, transparent 60%)",
        }}
      />
      <div className="max-w-5xl mx-auto px-6 md:px-10 relative">
        <div className="flex items-center justify-center gap-5 md:gap-8">
          {/* left accent: short teal rule + dot */}
          <div className="flex items-center gap-2 flex-shrink-0" aria-hidden="true">
            <span className="hidden sm:block h-px w-10 md:w-20 bg-gradient-to-r from-transparent to-[#2CA8A0]" />
            <span className="h-1.5 w-1.5 rounded-full bg-[#E8A830]" />
          </div>

          <p
            className="font-display italic text-[#F9F6EF] text-2xl sm:text-3xl md:text-4xl leading-tight text-center tracking-tight"
            data-testid="social-proof-tagline"
          >
            Built by expats, for expats.
          </p>

          {/* right accent: mirrored */}
          <div className="flex items-center gap-2 flex-shrink-0" aria-hidden="true">
            <span className="h-1.5 w-1.5 rounded-full bg-[#E8A830]" />
            <span className="hidden sm:block h-px w-10 md:w-20 bg-gradient-to-l from-transparent to-[#2CA8A0]" />
          </div>
        </div>

        <p
          className="font-mono text-[10px] tracking-[0.18em] text-[#E8A830] text-center mt-4 uppercase"
          data-testid="social-proof-attribution"
        >
          - Francisco &amp; Rodrigo, Co-founders
        </p>
      </div>
    </section>
  );
}

/* ============== INTERACTIVE TOOL STRIP (iter 137) ==============
   Three cards. Each chip taps through to the matching tool with the
   user's answer pre-selected via URL param (already supported in
   /mexico-readiness-score, /mexico-cost-of-living-calculator, and
   /mexico-visa-finder per the iter 136 build). Chip styling mirrors
   the answer-card design on the full tool pages exactly. */
function ToolStrip() {
  const navigate = useNavigate();
  // 200ms amber-highlight feedback before navigation, so the user sees
  // the same visual confirmation as on the full tool pages.
  const [tappedKey, setTappedKey] = useState(null);
  // iter 137b - Every chip tap fires GA4 tool_chip_clicked BEFORE the
  // redirect. `tool` is the destination tool ID, `value` is the chip
  // label so the data is human-readable in GA4 reports.
  const tap = (key, href, { tool, value }) => {
    trackToolChipClicked({ tool, value });
    setTappedKey(key);
    setTimeout(() => navigate(href), 200);
  };

  const moveChips = [
    { v: 'A', label: 'Just me' },
    { v: 'B', label: 'Me and my partner' },
    { v: 'C', label: 'Me, partner and kids' },
    { v: 'D', label: 'Me and a family member' },
  ];
  const costChips = [
    { id: 'mexico_city', label: 'Mexico City' },
    { id: 'merida', label: 'Merida' },
    { id: 'puerto_vallarta', label: 'Puerto Vallarta' },
    { id: 'oaxaca', label: 'Oaxaca' },
    { id: 'san_miguel', label: 'San Miguel' },
    { id: null, label: 'More cities →' },
  ];
  const visaChips = [
    { v: 'A', label: 'Remote worker' },
    { v: 'B', label: 'Retired' },
    { v: 'C', label: 'Mexican family connection' },
    { v: 'D', label: 'Investor' },
    { v: 'H', label: 'Just exploring' },
  ];

  return (
    <section
      data-testid="tool-strip"
      className="bg-[#F9F6EF] py-14 px-6 md:px-10"
      aria-label="Free tools"
    >
      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">

        {/* CARD 1 - MOVE SCORE - teal background, white text */}
        <div
          data-testid="tool-strip-card-readiness"
          className="bg-[#2CA8A0] rounded-2xl p-8 flex flex-col min-h-[280px]"
        >
          <p className="font-mono uppercase tracking-[0.2em] text-[10px] text-white/90 mb-3">
            Free Assessment
          </p>
          <h3 className="font-display italic text-3xl md:text-4xl text-white leading-[1.05] mb-4">
            Are you ready to move to Mexico?
          </h3>
          <p className="text-sm text-white/85 mb-5">Who is making this move with you?</p>
          <div className="flex flex-wrap gap-2 mb-5">
            {moveChips.map((c) => {
              const key = `move-${c.v}`;
              const tapped = tappedKey === key;
              return (
                <button
                  key={c.v}
                  type="button"
                  data-testid={`strip-move-chip-${c.v}`}
                  onClick={() => tap(key, `/mexico-readiness-score?start=q3&q3=${c.v}`, { tool: 'move_score', value: c.label })}
                  className={`text-sm px-4 py-2 rounded-xl border-2 transition ${
                    tapped
                      ? 'bg-[#E8A830] border-[#E8A830] text-[#0A1F2E]'
                      : 'bg-transparent border-white text-white hover:bg-white/10'
                  }`}
                >
                  {c.label}
                </button>
              );
            })}
          </div>
          <p className="mt-auto text-sm italic text-white/85">See your full score →</p>
        </div>

        {/* CARD 2 - COST CALCULATOR - cream w/ navy border, navy text */}
        <div
          data-testid="tool-strip-card-cost"
          className="bg-[#F9F6EF] border border-[#0A1F2E] rounded-2xl p-8 flex flex-col min-h-[280px]"
        >
          <p className="font-mono uppercase tracking-[0.2em] text-[10px] text-[#0A1F2E] mb-3">
            Free Calculator
          </p>
          <h3 className="font-display italic text-3xl md:text-4xl text-[#0A1F2E] leading-[1.05] mb-4">
            What will your life in Mexico actually cost?
          </h3>
          <p className="text-sm text-[#0A1F2E]/65 mb-5">Which city are you considering?</p>
          <div className="flex flex-wrap gap-2 mb-5">
            {costChips.map((c) => {
              const key = `cost-${c.id || 'more'}`;
              const tapped = tappedKey === key;
              const href = c.id
                ? `/mexico-cost-of-living-calculator?city=${c.id}`
                : '/mexico-cost-of-living-calculator';
              return (
                <button
                  key={key}
                  type="button"
                  data-testid={`strip-cost-chip-${c.id || 'more'}`}
                  onClick={() => tap(key, href, { tool: 'cost_calculator', value: c.label })}
                  className={`text-sm px-4 py-2 rounded-xl border-2 transition ${
                    tapped
                      ? 'bg-[#E8A830] border-[#E8A830] text-[#0A1F2E]'
                      : 'bg-transparent border-[#0A1F2E] text-[#0A1F2E] hover:bg-[#0A1F2E]/5'
                  }`}
                >
                  {c.label}
                </button>
              );
            })}
          </div>
          <p className="mt-auto text-sm italic text-[#0A1F2E]/65">Calculate your full budget →</p>
        </div>

        {/* CARD 3 - VISA FINDER - navy background, white text */}
        <div
          data-testid="tool-strip-card-visa"
          className="bg-[#0A1F2E] rounded-2xl p-8 flex flex-col min-h-[280px]"
        >
          <p className="font-mono uppercase tracking-[0.2em] text-[10px] text-[#E8A830] mb-3">
            Free Tool
          </p>
          <h3 className="font-display italic text-3xl md:text-4xl text-white leading-[1.05] mb-4">
            Which Mexico visa do you actually need?
          </h3>
          <p className="text-sm text-white/85 mb-5">Which best describes your situation?</p>
          <div className="flex flex-wrap gap-2 mb-5">
            {visaChips.map((c) => {
              const key = `visa-${c.v}`;
              const tapped = tappedKey === key;
              return (
                <button
                  key={c.v}
                  type="button"
                  data-testid={`strip-visa-chip-${c.v}`}
                  onClick={() => tap(key, `/mexico-visa-finder?q1=${c.v}`, { tool: 'visa_finder', value: c.label })}
                  className={`text-sm px-4 py-2 rounded-xl border-2 transition ${
                    tapped
                      ? 'bg-[#E8A830] border-[#E8A830] text-[#0A1F2E]'
                      : 'bg-transparent border-white text-white hover:bg-white/10'
                  }`}
                >
                  {c.label}
                </button>
              );
            })}
          </div>
          <p className="mt-auto text-sm italic text-[#E8A830]">Get your visa recommendation →</p>
        </div>

      </div>
    </section>
  );
}

/* ============== THE MOVE (Sea Sand) ============== */
const MOVE_CARDS = [
  { id: 'blueprint', icon: Sparkles, title: 'Blueprint AI', body: 'A 40-page roadmap built around your answers, not a template. Visa path, city match, budget, timeline. Read it on the plane.', tag: 'From $199' },
  { id: 'checklist', icon: CheckSquare, title: 'Living Checklist', body: 'Every step from your first apostille to your CFE bill, organized by phase. Tick items, share with your spouse, never forget what comes next.', tag: 'Included with Essentials' },
  { id: 'classroom', icon: GraduationCap, title: 'The Classroom', body: 'Short, founder-led courses on visas, taxes, real estate, language, and surviving your first 90 days. New modules every month.', tag: 'Included with Essentials' },
];

function TheMove() {
  return (
    <section id="how" data-testid="the-move-section" className="bg-[#F9F6EF] py-16 md:py-20" aria-labelledby="the-move-heading">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="mb-14 md:mb-20 max-w-3xl">
          <p className="font-mono uppercase tracking-[0.18em] text-[10px] md:text-xs text-[#1A5C6E] mb-4">Section 01 · The Move</p>
          <h2 id="the-move-heading" className="font-display text-4xl md:text-6xl text-[#0A1F2E] leading-[1.05] mb-5">
            Before you <span className="italic">land</span> - everything in one place.
          </h2>
          <p className="font-body font-light text-lg md:text-xl text-[#0A1F2E]/75 leading-relaxed">
            The three things every expat needs the moment they decide to go. Bundled into Mundalo's one-time products - built once, yours forever.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
          {MOVE_CARDS.map((c) => {
            const Icon = c.icon;
            return (
              <article
                key={c.id}
                data-testid={`move-card-${c.id}`}
                className="bg-[#F4ECD8] rounded-2xl p-8 md:p-10 flex flex-col h-full transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_8px_30px_rgba(10,31,46,0.06)]"
              >
                <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center mb-7">
                  <Icon className="w-6 h-6 text-[#2CA8A0]" strokeWidth={1.5} />
                </div>
                <h3 className="font-display text-3xl text-[#0A1F2E] mb-3 leading-tight">{c.title}</h3>
                <p className="font-body font-light text-[15px] md:text-base text-[#0A1F2E]/75 leading-relaxed flex-1">{c.body}</p>
                <p className="font-mono uppercase tracking-[0.15em] text-[10px] text-[#1A5C6E] mt-6 pt-5 border-t border-[#0A1F2E]/8">
                  {c.tag}
                </p>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ============== THE LIFE (Pacific dark panel) ============== */
const LIFE_FEATURES = [
  { icon: Users, title: 'Community Board', body: 'A private feed of expats already living it. Ask. Answer. Never feel lost.' },
  { icon: Calendar, title: 'Events & Meetups', body: 'Real-world dinners, hikes, language nights - every weekend in CDMX, Mérida, Tulum, SMA.' },
  { icon: HomeIcon, title: 'Housing Finder', body: 'Vetted long-term rentals from members who already lived in them. No scams. No price gouging.' },
  { icon: Tag, title: 'Local Discounts', body: 'Member rates at gyms, restaurants, co-works, and clinics across Mexico.' },
  { icon: Activity, title: 'Curated Activities', body: 'Monthly experiences: cenote dives, tequila farms, language immersion, art walks.' },
  { icon: Handshake, title: 'Partner Deals', body: 'Stripe-tier discounts on visas, movers, vets, real estate lawyers - pre-negotiated.' },
  { icon: Globe, title: 'City Guides', body: 'Honest, full-depth guides for 13 Mexican cities - budgets, neighborhoods, schools, healthcare, the move-in plan.' },
];

/* --- iter 97 --------------------------------------------------------------
   "Why Mundalo / What's Included" - clear, scannable breakdown of the five
   product pillars (Blueprint, Vetted Partners, Local Deals, Community,
   Classroom). Added immediately after the social-proof strip so prospects
   land on differentiation before pricing.

   Cream surface to break the rhythm between the warm Hero/SocialProof and
   the cobalt "The Life" section that follows. Lucide icons, Cormorant
   italic headlines, mono eyebrow.
---------------------------------------------------------------------------- */
const WHATS_INCLUDED_PILLARS = [
  {
    icon: FileText,
    title: 'Your personalized Blueprint',
    body: "A premium $300-feel relocation plan written for YOU - visa pathway, neighborhood deep dive in your destination city, budget tables, healthcare options (IMSS vs private), insurance recommendations, schools, banking, and a 30-day action checklist. 19 sections, all city-specific.",
    badge: 'Day 1',
  },
  {
    icon: BadgeCheck,
    title: 'Vetted partners',
    body: 'Hand-picked immigration lawyers, accountants, movers, real-estate agents, and insurance brokers we have personally worked with. You skip the cold outreach and the bad first hire - we already filtered out the duds.',
    badge: 'Trusted',
  },
  {
    icon: Tag,
    title: 'Local deals & discounts',
    body: "Members-only pricing from partner restaurants, gyms, language schools, coworking spaces, dental clinics, and experience providers across all 15 cities we cover. Pays for the membership in the first month.",
    badge: '15 cities',
  },
  {
    icon: Users,
    title: 'The Mundalo community',
    body: 'Direct access to 2,500+ Americans and Canadians who have already made the move. Ask the real questions, get answers from people in your exact city, RSVP to local meetups, and stop relying on outdated Facebook groups.',
    badge: '2,500+ members',
  },
  {
    icon: GraduationCap,
    title: 'The Classroom',
    body: "Recorded master-classes on visas, taxes, real-estate, healthcare enrollment, schooling, and integration. Watch on your timeline. New lessons added monthly - every one is opinionated, current, and 20 minutes or less.",
    badge: 'Self-paced',
  },
];

function WhatsIncluded() {
  return (
    <section
      data-testid="whats-included-section"
      className="bg-[#F9F6EF] py-16 md:py-20"
      aria-labelledby="whats-included-heading"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="mb-14 md:mb-20 max-w-3xl">
          <p className="font-mono uppercase tracking-[0.18em] text-[10px] md:text-xs text-[#1A5C6E] mb-4">
            Why Mundalo
          </p>
          <h2
            id="whats-included-heading"
            className="font-display text-4xl md:text-6xl text-[#0A1F2E] leading-[1.05] mb-5"
          >
            Everything you need, <span className="italic text-[#1A5C6E]">in one place.</span>
          </h2>
          <p className="font-body font-light text-lg md:text-xl text-[#0A1F2E]/75 leading-relaxed">
            Other "expat guides" are PDFs. Mundalo is a platform - built specifically for moving to Mexico, with the people, partners, and answers you actually need.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {WHATS_INCLUDED_PILLARS.map((pillar, i) => {
            const Icon = pillar.icon;
            return (
              <article
                key={i}
                data-testid={`whats-included-card-${i}`}
                className="bg-white border border-[#EFE6CF] rounded-2xl p-7 md:p-8 hover:border-[#2CA8A0]/60 hover:-translate-y-0.5 transition-all"
              >
                <div className="flex items-center justify-between mb-5">
                  <div className="w-12 h-12 rounded-xl bg-[#FCF8EE] border border-[#EFE6CF] flex items-center justify-center text-[#1A5C6E]">
                    <Icon className="w-6 h-6" strokeWidth={1.75} />
                  </div>
                  <span className="font-mono uppercase text-[10px] tracking-[0.15em] text-[#2CA8A0]">
                    {pillar.badge}
                  </span>
                </div>
                <h3 className="font-display italic text-2xl text-[#0A1F2E] mb-3 leading-tight">
                  {pillar.title}
                </h3>
                <p className="font-body text-[15px] text-[#0A1F2E]/75 leading-relaxed">
                  {pillar.body}
                </p>
              </article>
            );
          })}

          {/* 6th tile - a CTA card so the grid stays balanced at 3×2 on lg */}
          <Link
            to="/portal"
            data-testid="whats-included-cta"
            className="group bg-[#0A1F2E] hover:bg-[#1A5C6E] text-white border border-[#0A1F2E] rounded-2xl p-7 md:p-8 transition-all flex flex-col justify-between min-h-[230px]"
          >
            <div>
              <div className="w-12 h-12 rounded-xl bg-white/10 border border-white/20 flex items-center justify-center mb-5">
                <Sparkles className="w-6 h-6 text-[#E8A830]" strokeWidth={1.75} />
              </div>
              <h3 className="font-display italic text-2xl mb-3 leading-tight">
                Start free for 15 days.
              </h3>
              <p className="font-body text-[15px] text-white/75 leading-relaxed">
                Card at signup, then $9.99/month. Cancel anytime from your account portal.
              </p>
            </div>
            <span className="font-mono uppercase text-[11px] tracking-[0.15em] text-[#E8A830] mt-6 inline-flex items-center gap-2 group-hover:gap-3 transition-all">
              Start free - 15 days <ArrowRight className="w-4 h-4" />
            </span>
          </Link>
        </div>
      </div>
    </section>
  );
}

function TheLife() {
  return (
    <section
      data-testid="the-life-section"
      className="bg-[#1A5C6E] py-24 md:py-32 relative overflow-hidden"
      aria-labelledby="the-life-heading"
    >
      <div className="absolute -top-20 -right-20 w-96 h-96 rounded-full bg-[#E8A830]/8 blur-3xl pointer-events-none" aria-hidden="true" />
      <div className="absolute -bottom-20 -left-20 w-96 h-96 rounded-full bg-[#2CA8A0]/8 blur-3xl pointer-events-none" aria-hidden="true" />

      <div className="max-w-7xl mx-auto px-6 md:px-10 relative">
        <div className="mb-14 md:mb-20 max-w-3xl">
          <p className="font-mono uppercase tracking-[0.18em] text-[10px] md:text-xs text-[#E8A830] mb-4">Section 02 · The Life</p>
          <h2 id="the-life-heading" className="font-display text-4xl md:text-6xl text-white leading-[1.05] mb-5">
            After you land - <span className="italic text-[#E8A830]">live freely.</span>
          </h2>
          <p className="font-body font-light text-lg md:text-xl text-white/75 leading-relaxed">
            The people, the housing, the rituals, the local secrets. Eight ways Mundalo turns your new country into home.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
          {LIFE_FEATURES.map((f, i) => {
            const Icon = f.icon;
            return (
              <article
                key={f.title}
                data-testid={`life-feature-${i}`}
                className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 md:p-7 transition-all duration-300 hover:bg-white/10 hover:-translate-y-1"
              >
                <Icon className="w-5 h-5 text-[#E8A830] mb-5" strokeWidth={1.5} />
                <h3 className="font-display text-2xl text-white mb-2 leading-tight">{f.title}</h3>
                <p className="font-body font-light text-sm text-white/70 leading-relaxed">{f.body}</p>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ============== PRICING STRIP ============== */
// iter 119 - exported so the standalone /pricing route can reuse the
// exact same markup without forking. The component renders fine on its
// own (no NewHome-specific layout deps).
export function Pricing() {
  const [loading, setLoading] = useState(null);
  const [billing, setBilling] = useState('monthly'); // monthly | annual - applies to community + connected only
  const navigate = useNavigate();

  // iter 118 - fire GA4 `view_pricing` when the pricing section
  // becomes >= 30% visible. Uses sessionStorage so a user scrolling
  // up-and-down (or revisiting the homepage in the same session)
  // doesn't inflate the count.
  useEffect(() => {
    const node = document.getElementById('pricing');
    if (!node || typeof IntersectionObserver === 'undefined') return;
    try {
      if (window.sessionStorage && window.sessionStorage.getItem('ga4_view_pricing_fired')) return;
    } catch { /* swallow */ }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          try {
            trackViewPricing();
            if (window.sessionStorage) window.sessionStorage.setItem('ga4_view_pricing_fired', '1');
          } catch { /* analytics is fire-and-forget */ }
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  // iter 99 - paid tiers now route through the dedicated /checkout/:tier
  // page so users create an account BEFORE Stripe (option B per the
  // 2026-02-23 product call). Anonymous direct-to-Stripe was leaving
  // orphaned purchases without a Mundalo account.
  const startCheckout = (tierId) => {
    setLoading(tierId);
    const isSub = tierId === 'community' || tierId === 'connected';
    const period = isSub ? billing : 'onetime';
    navigate(`/checkout/${tierId}?period=${period}`);
  };

  // iter 187.j - Annual savings now expressed as a PERCENTAGE sourced
  // from constants/annualSavings.js SSoT. Dollar-based badges left
  // stale merge tags on the toggle pill.
  const perCardSavingsCopy = (tierId) => formatPerCardSavings(tierId);

  // ---- TIER GROUPING (Wave 2) ----
  // THE MOVE - one-time products that get you on the ground.
  // THE LIFE - recurring memberships that keep you connected once you're there.
  // Iter 145 - Connected hidden from pricing (Section 3 / spec).
  const moveTiers = ['blueprint_only', 'essentials', 'guided'];
  const lifeTiers = ['community'];

  // Render a single pricing card (extracted so THE MOVE + THE LIFE rows share it).
  const renderCard = (tierId) => {
    const t = TIERS[tierId];
    const isPopular = tierId === 'guided';
    const isEntry = tierId === 'blueprint_only';
    const isSub = tierId === 'community' || tierId === 'connected';
    const showAnnual = isSub && billing === 'annual';
    const displayPrice = showAnnual ? t.priceAnnual : t.price;
    const displayInterval = showAnnual ? 'year' : t.interval;
    const savingsCopy = isSub ? perCardSavingsCopy(tierId) : '';

    // Iter 176 - tier tagline, blurb, and bullets now come from a single
    // source of truth in constants/pricingContent.js for the three paid
    // one-time tiers (Blueprint Only / Essentials / Guided). Community and
    // Connected still use inline copy because they are subscription plans
    // owned by a different product surface.
    const pc = PRICING_CONTENT[tierId] || null;

    const anchorCopy =
      pc ? pc.tagline :
      tierId === 'community'      ? "You moved. Now don't move alone." :
      tierId === 'connected'      ? 'Everything inside. Forever.' :
      null;

    const tierBlurb =
      pc ? pc.description :
      tierId === 'community'      ? 'The Community Board, Local Deals, in-person and online events, and the housing + roommate finder. Cancel anytime.' :
      'Community plus monthly expert Q&A and Connected-only events.';

    const featuresList =
      pc ? pc.bullets :
      tierId === 'community' ? [
        'Community Board + Newcomers Board',
        'Local Deals - exclusive discounts at local businesses',
        'Book Local Adventures - curated experiences',
        'Events (in-person + online)',
        'Housing listings + roommate finder',
      ] :
      ['Community - included','Monthly expert Q&A','Connected-only events','Priority housing notifications'];

    const badgeLabel =
      isPopular ? 'Most Popular' :
      isEntry   ? 'Start here' :
      null;
    const ringClass =
      isPopular ? 'ring-2 ring-[#E8A830] shadow-[0_8px_30px_rgba(232,168,48,0.18)] md:-translate-y-3' :
      isEntry   ? 'ring-2 ring-[#2CA8A0]/60 shadow-[0_6px_22px_rgba(44,168,160,0.12)]' :
      'border border-[#EFE6CF] hover:border-[#2CA8A0]/40';

    return (
      <article
        key={tierId}
        data-testid={`pricing-tier-${tierId}`}
        className={`bg-white rounded-2xl p-7 md:p-8 flex flex-col h-full relative transition-all duration-300 ${ringClass}`}
      >
        {badgeLabel && (
          <span
            data-testid={`pricing-tier-${tierId}-badge`}
            className={`absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] font-mono uppercase tracking-widest px-3 py-1 rounded-full whitespace-nowrap font-semibold ${
              isPopular ? 'bg-[#E8A830] text-[#0A1F2E]' : 'bg-[#2CA8A0] text-white'
            }`}
          >
            {badgeLabel}
          </span>
        )}
        <h3 className="font-display text-3xl text-[#0A1F2E]">{t.name}</h3>
        <div className="flex items-baseline gap-1 mt-2 mb-1">
          <span
            className="font-display text-4xl text-[#0A1F2E]"
            data-testid={`pricing-tier-${tierId}-price`}
          >
            ${displayPrice}
          </span>
          <span className="font-body text-sm text-[#0A1F2E]/55">/{displayInterval}</span>
        </div>
        {showAnnual && savingsCopy && (
          <p
            className="text-[11px] font-mono uppercase tracking-[0.12em] text-[#E8A830] font-semibold mb-3"
            data-testid={`pricing-tier-${tierId}-savings`}
          >
            {savingsCopy}
          </p>
        )}
        {!(showAnnual && savingsCopy) && <div className="mb-3" />}
        <p
          className="font-body text-[13px] md:text-sm text-[#1A5C6E] italic leading-snug mb-3"
          data-testid={`pricing-tier-${tierId}-anchor`}
        >
          {anchorCopy}
        </p>
        <p className="font-body font-light text-sm text-[#0A1F2E]/70 leading-relaxed mb-6">{tierBlurb}</p>

        <ul className="space-y-2 mb-7 flex-1">
          {featuresList.map((feat) => (
            <li key={feat} className="flex items-start gap-2">
              <Check className="w-4 h-4 mt-0.5 text-[#2CA8A0] shrink-0" strokeWidth={2.5} />
              <span className="font-body text-sm text-[#0A1F2E]/85">{feat}</span>
            </li>
          ))}
        </ul>

        <button
          data-testid={`pricing-cta-${tierId}`}
          onClick={() => startCheckout(tierId)}
          disabled={loading === tierId}
          className={`w-full py-3 rounded-full font-medium transition-all ${
            isPopular
              ? 'bg-[#E8A830] hover:brightness-95 text-[#0A1F2E]'
              : isEntry
                ? 'bg-[#2CA8A0] hover:brightness-95 text-white'
                : 'bg-[#0A1F2E] hover:bg-[#1A5C6E] text-white'
          } ${loading === tierId ? 'opacity-60' : ''}`}
        >
          {loading === tierId ? 'Opening checkout…' : t.cta}
        </button>
      </article>
    );
  };

  return (
    <section id="pricing" data-testid="pricing-section" className="bg-[#F9F6EF] py-16 md:py-20" aria-labelledby="pricing-heading">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="text-center mb-10 max-w-3xl mx-auto">
          <p className="font-mono uppercase tracking-[0.18em] text-[10px] md:text-xs text-[#1A5C6E] mb-4">Pricing · two journeys</p>
          <h2 id="pricing-heading" className="font-display text-4xl md:text-6xl text-[#0A1F2E] leading-[1.05] mb-5">
            Get there. <span className="italic">Stay there.</span>
          </h2>
          <p className="font-body font-light text-lg text-[#0A1F2E]/75">
            Pay once to plan the move. Pay monthly to live the life. Most members do both.
          </p>
        </div>

        {/* Monthly / Annual billing toggle - applies to Community & Connected only */}
        <div
          className="mx-auto mb-10 max-w-md flex items-center justify-center bg-white rounded-full p-1 border border-[#EFE6CF] shadow-sm"
          role="tablist"
          aria-label="Billing period"
          data-testid="billing-toggle"
        >
          <button
            type="button"
            role="tab"
            aria-selected={billing === 'monthly'}
            onClick={() => setBilling('monthly')}
            data-testid="billing-toggle-monthly"
            className={`flex-1 py-2.5 rounded-full font-mono uppercase text-[11px] tracking-[0.12em] transition-all ${
              billing === 'monthly'
                ? 'bg-[#F9F6EF] text-[#0A1F2E] border-2 border-[#2CA8A0] font-semibold'
                : 'text-[#0A1F2E]/60 hover:text-[#0A1F2E]'
            }`}
          >
            Monthly
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={billing === 'annual'}
            onClick={() => setBilling('annual')}
            data-testid="billing-toggle-annual"
            className={`flex-1 py-2.5 rounded-full font-mono uppercase text-[11px] tracking-[0.12em] transition-all flex items-center justify-center gap-2 ${
              billing === 'annual'
                ? 'bg-[#F9F6EF] text-[#0A1F2E] border-2 border-[#2CA8A0] font-semibold'
                : 'text-[#0A1F2E]/60 hover:text-[#0A1F2E]'
            }`}
          >
            Annual
            <span
              className="inline-flex items-center bg-[#E8A830] text-[#0A1F2E] text-[9px] font-bold px-2 py-0.5 rounded-full"
              data-testid="billing-toggle-save-badge"
            >
              {formatSavingsBadge()}
            </span>
          </button>
        </div>

        {/* Free trial banner - iter 182.g copy: card-captured subscription */}
        <div className="max-w-3xl mx-auto mb-12 md:mb-16 bg-[#F4ECD8] rounded-2xl p-6 md:p-7 flex flex-col md:flex-row items-center gap-5 md:gap-7" data-testid="free-trial-banner">
          <div className="flex-1 text-center md:text-left">
            <p className="font-mono uppercase tracking-[0.15em] text-[10px] text-[#1A5C6E] mb-2">Start here · 15 days on us</p>
            <h3 className="font-display text-3xl text-[#0A1F2E] mb-1">15 days free. <span className="italic">Then $9.99/month.</span></h3>
            <p className="font-body font-light text-[15px] text-[#0A1F2E]/75">Community, Classroom & Vetted Partners. Cancel anytime.</p>
          </div>
          <Link
            to="/start-trial"
            data-testid="pricing-free-trial-cta"
            className="inline-flex items-center gap-2 bg-[#E8A830] hover:brightness-95 text-[#0A1F2E] font-medium px-6 py-3 rounded-full whitespace-nowrap"
          >
            Start free - 15 days <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {/* ===================== THE MOVE ===================== */}
        <div className="mb-16 md:mb-20" data-testid="pricing-the-move">
          <div className="mb-8 md:mb-10 max-w-3xl">
            <p className="font-mono uppercase tracking-[0.18em] text-[10px] md:text-xs text-[#E8A830] mb-3">THE MOVE · One-time</p>
            <h3 className="font-display text-3xl md:text-5xl text-[#0A1F2E] leading-[1.05] mb-4">
              Trade six months of forums for <span className="italic">one weekend of clarity.</span>
            </h3>
            <p className="font-body font-light text-base md:text-lg text-[#0A1F2E]/75 leading-relaxed max-w-2xl">
              Pay once. Get the plan. Move with confidence. The longer you wait, the longer you stay where you don't want to be.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
            {moveTiers.map(renderCard)}
          </div>

          {/* iter 187.h.1 - Locked "Covers Everything" Guarantee. Verbatim.
              Applies to the three one-time tiers above ($199 / $349 / $549). */}
          <div
            data-testid="covers-everything-guarantee"
            className="mt-8 md:mt-10 max-w-3xl mx-auto rounded-2xl border border-[#E8A830]/40 bg-[#FBF6E8] p-6 md:p-7"
          >
            <p className="font-mono uppercase tracking-[0.15em] text-[10px] text-[#7E5A12] mb-2">
              Our promise
            </p>
            <h4 className="font-display text-2xl text-[#0A1F2E] mb-3">
              The &ldquo;Covers Everything&rdquo; Guarantee
            </h4>
            <p className="font-body text-[15px] text-[#0A1F2E]/85 leading-relaxed">
              If we don&rsquo;t answer everything you need to actually make this move - your visa, your budget, your city, your family - tell us. We&rsquo;ll fix it, free, until we do. If it still doesn&rsquo;t cover what you need, we&rsquo;ll refund you.
            </p>
          </div>
        </div>

        {/* ===================== THE LIFE ===================== */}
        <div data-testid="pricing-the-life">
          <div className="mb-8 md:mb-10 max-w-3xl">
            <p className="font-mono uppercase tracking-[0.18em] text-[10px] md:text-xs text-[#2CA8A0] mb-3">THE LIFE · Monthly or annual</p>
            <h3 className="font-display text-3xl md:text-5xl text-[#0A1F2E] leading-[1.05] mb-4">
              The hardest part isn't the move. <span className="italic">It's month three.</span>
            </h3>
            <p className="font-body font-light text-base md:text-lg text-[#0A1F2E]/75 leading-relaxed max-w-2xl">
              The novelty fades. Your friends are 2,000 miles away. Mundalo's community is the difference between staying and going home.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6 max-w-4xl">
            {lifeTiers.map(renderCard)}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ============== TESTIMONIAL ============== */
function Testimonial() {
  return (
    <section data-testid="testimonial" className="bg-[#F4ECD8] py-16 md:py-20" aria-label="Member testimonial">
      <div className="max-w-4xl mx-auto px-6 md:px-10 text-center">
        <p className="font-mono uppercase tracking-[0.18em] text-[10px] md:text-xs text-[#1A5C6E] mb-6">From a recent member</p>
        <blockquote className="font-display italic text-3xl md:text-5xl text-[#0A1F2E] leading-[1.15] mb-8">
          “I spent six months reading expat forums and getting nowhere. Mundalo handed me a 47-step checklist on day one. I was sleeping in Mérida nine weeks later.”
        </blockquote>
        <div className="flex items-center justify-center gap-3">
          <img src={AVATARS[2]} alt="" className="w-12 h-12 rounded-full object-cover" />
          <div className="text-left">
            <p className="font-body font-medium text-[#0A1F2E]">Sofía R.</p>
            <p className="font-body font-light text-sm text-[#0A1F2E]/65">Software engineer · Mérida, Yucatán</p>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ============== 90-DAY QUOTE (iter 144w) ==============
   Single-line dark-background punctuation that sits directly under the
   member testimonial. No attribution, no button - just the line and a
   thin amber underline. Reads as the calm closing thought before the
   site footer. */
function NinetyDayQuote() {
  return (
    <section
      data-testid="ninety-day-quote"
      className="bg-[#0D1F2D] py-12"
      aria-label="Ninety day quote"
    >
      <div className="max-w-3xl mx-auto px-6 text-center">
        <p className="font-display italic text-[#F4ECD8] text-2xl md:text-3xl lg:text-4xl leading-snug">
          Most people who decide to make this move are living in Mexico within 90 days.
        </p>
        <div
          aria-hidden="true"
          className="mx-auto mt-8 h-px bg-[#E8A830]"
          style={{ width: '60px' }}
        />
      </div>
    </section>
  );
}

/* ============== FOOTER ============== */
function Footer() {
  return (
    <footer data-testid="footer" className="bg-[#0A1F2E] text-white pt-20 pb-10" aria-label="Mundalo site footer">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-10 md:gap-8 mb-16">
          <div className="col-span-2">
            <Link to="/" className="inline-block mb-3"><img src="https://res.cloudinary.com/des9fk8et/image/upload/e_trim,f_auto,q_auto:good/mundalo/brand/mundalo-logo-white.png" alt="Mundalo" className="block h-10 md:h-12 w-[200px] md:w-[240px] object-contain object-left" /></Link>
            <p className="font-display italic text-xl text-[#E8A830] mb-5">Move Abroad. Live Freely.</p>
            <p className="font-body font-light text-sm text-white/65 max-w-xs leading-relaxed">
              The members-only relocation platform for people moving to Mexico. Built by expats, for expats.
            </p>
          </div>

          <div>
            <p className="font-mono uppercase text-[10px] tracking-[0.15em] text-white/40 mb-4">The Move</p>
            <ul className="space-y-2.5">
              <li><Link to="/relocation/blueprint" className="font-body text-sm text-white/80 hover:text-[#E8A830] transition">Blueprint AI</Link></li>
              <li><Link to="/relocation/checklist" className="font-body text-sm text-white/80 hover:text-[#E8A830] transition">Checklist</Link></li>
              <li><Link to="/portal/classroom" className="font-body text-sm text-white/80 hover:text-[#E8A830] transition">Classroom</Link></li>
              <li><Link to="/relo-news" className="font-body text-sm text-white/80 hover:text-[#E8A830] transition">Relo News</Link></li>
            </ul>
          </div>

          <div>
            <p className="font-mono uppercase text-[10px] tracking-[0.15em] text-white/40 mb-4">The Life</p>
            <ul className="space-y-2.5">
              <li><Link to="/portal/community" className="font-body text-sm text-white/80 hover:text-[#E8A830] transition">Community</Link></li>
              <li><Link to="/portal/events" className="font-body text-sm text-white/80 hover:text-[#E8A830] transition">Events</Link></li>
              <li><Link to="/portal/housing" className="font-body text-sm text-white/80 hover:text-[#E8A830] transition">Housing</Link></li>
              <li><Link to="/listings" className="font-body text-sm text-white/80 hover:text-[#E8A830] transition">Listings</Link></li>
            </ul>
          </div>

          <div>
            <p className="font-mono uppercase text-[10px] tracking-[0.15em] text-white/40 mb-4">Mundalo</p>
            <ul className="space-y-2.5">
              <li><a href="#pricing" className="font-body text-sm text-white/80 hover:text-[#E8A830] transition">Pricing</a></li>
              <li><Link to="/contact" className="font-body text-sm text-white/80 hover:text-[#E8A830] transition">Contact</Link></li>
              <li><Link to="/portal" className="font-body text-sm text-white/80 hover:text-[#E8A830] transition">Member Sign In</Link></li>
              <li><a href="mailto:hello@mundalo.com" data-testid="footer-email" className="font-body text-sm text-[#E8A830] hover:underline">hello@mundalo.com</a></li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/10 pt-7 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="font-body font-light text-xs text-white/45">© 2026 Mundalo. All rights reserved. · mundalo.com</p>
          <div className="flex items-center gap-6">
            <Link to="/privacy" className="font-body text-xs text-white/60 hover:text-white transition">Privacy</Link>
            <Link to="/terms" className="font-body text-xs text-white/60 hover:text-white transition">Terms</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

/* ============== PAGE ============== */
export default function NewHome() {
  // Per-page SEO - sets title, description, OG, canonical
  useEffect(() => {
    document.title = 'Move to Mexico - Your Complete Relocation Platform | Mundalo';
    const setMeta = (selector, content) => {
      let el = document.querySelector(selector);
      if (!el) {
        el = document.createElement('meta');
        if (selector.includes('property=')) el.setAttribute('property', selector.match(/property="([^"]+)"/)[1]);
        else if (selector.includes('name=')) el.setAttribute('name', selector.match(/name="([^"]+)"/)[1]);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };
    setMeta('meta[name="description"]', 'Everything Americans need to move to Mexico. Personalized Blueprint, expert guides, vetted partners, and a community of expats already living it. Start free.');
    setMeta('meta[property="og:title"]', 'Move to Mexico - Your Complete Relocation Platform | Mundalo');
    setMeta('meta[property="og:description"]', 'Everything Americans need to move to Mexico. Personalized Blueprint, expert guides, vetted partners, and a community of expats already living it. Start free.');
    setMeta('meta[property="og:type"]', 'website');
    setMeta('meta[property="og:url"]', 'https://mundalo.com/');
    setMeta('meta[property="og:image"]', HERO_IMG);
    let canon = document.querySelector('link[rel="canonical"]');
    if (!canon) { canon = document.createElement('link'); canon.rel = 'canonical'; document.head.appendChild(canon); }
    // iter 144j - self-referencing canonical. When NewHome serves as the
    // SPA catch-all (e.g., `/partners`, `/relocation/cdmx/<section>`),
    // we want the canonical to point at the URL the user actually
    // landed on, not at `/`. Strip the hash and trailing slash (except
    // for the root) for a clean, stable canonical value.
    const path = (typeof window !== 'undefined' && window.location && window.location.pathname) || '/';
    const cleanPath = path === '/' ? '/' : path.replace(/\/+$/, '');
    canon.href = `https://mundalo.com${cleanPath}`;
    // Keep og:url in sync so social shares of catch-all URLs reflect the page they're on.
    setMeta('meta[property="og:url"]', `https://mundalo.com${cleanPath}`);
  }, []);

  return (
    <div data-testid="home-root" className="bg-[#F9F6EF] min-h-screen">
      <Header />
      <Hero />
      <CostCalcEntry />
      <TheMove />
      <Pricing />
      {/* iter 187.i - PromoBanner moved from above the top nav to the
           bottom of the page. Commitment is still active - disclosure
           stays, just relocated below the pricing section so the top
           nav breathes and the CTA fits cleanly. */}
      <PromoBanner />
      <Footer />
    </div>
  );
}
